import { useState } from "react";
import { CheckCircle2, CalendarCheck, MessageCircle, Loader2, Send } from "lucide-react";
import { useLocation } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { FORM_SERVICES } from "@/data/services";
import { STATUS_OPTIONS, CONTACT_METHODS, bookingUrl, whatsappUrl } from "@/data/site";

const EMPTY = { name: "", phone: "", email: "", service: "", country: "", status_in_canada: "", work_permit_expiry: "", contact_method: "Phone", consultation_date: "", message: "", website: "" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your full name.";
  const digits = f.phone.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) e.phone = "Please enter a valid phone number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.service) e.service = "Please select a service.";
  if (f.country.trim().length < 2) e.country = "Please enter your current country.";
  if (f.message.trim().length < 10) e.message = "Please describe your situation (at least 10 characters).";
  return e;
};

export const Field = ({ label, name, required, error, children, testPrefix = "enquiry" }) => (
  <label className="block">
    <span className="mb-1.5 block text-[13px] font-semibold text-navy">{label}{required && <span className="text-brand-red"> *</span>}</span>
    {children}
    {error && <span className="mt-1 block text-xs font-medium text-brand-red" data-testid={`${testPrefix}-error-${name}`}>{error}</span>}
  </label>
);

const Success = ({ onReset }) => (
  <div className="py-6 text-center" data-testid="enquiry-success">
    <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" strokeWidth={1.5} />
    <h3 className="mt-4 font-display text-2xl text-navy">Thank you. Your enquiry has been sent.</h3>
    <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">Our team will review your information and contact you by your preferred method, usually within 1–2 business days. If your matter is urgent, such as a permit expiring soon, you can book a paid consultation or message us on WhatsApp.</p>
    <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
      <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid="enquiry-success-book"><CalendarCheck className="h-4 w-4" /> Book a Consultation</a>
      <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="btn-outline justify-center" data-testid="enquiry-success-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
    </div>
    <button onClick={onReset} className="mt-5 text-sm font-semibold text-navy underline underline-offset-4" data-testid="enquiry-send-another">Send another enquiry</button>
  </div>
);

export const EnquiryForm = ({ defaultService = "" }) => {
  const { pathname } = useLocation();
  const [form, setForm] = useState({ ...EMPTY, service: defaultService });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setServerError("");
    if (Object.keys(errs).length) return;
    setState("sending");
    try {
      await api.post("/enquiries", { ...form, lead_source: `Website: ${pathname}` });
      setState("done");
    } catch (err) {
      setServerError(errorText(err));
      setState("idle");
    }
  };

  if (state === "done") return <Success onReset={() => { setForm({ ...EMPTY, service: defaultService }); setState("idle"); }} />;

  const inp = (k) => `field ${errors[k] ? "!border-brand-red" : ""}`;

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="enquiry-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full Name" name="name" required error={errors.name}><input className={inp("name")} value={form.name} onChange={set("name")} autoComplete="name" data-testid="enquiry-name-input" /></Field>
        <Field label="Phone Number" name="phone" required error={errors.phone}><input className={inp("phone")} type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" data-testid="enquiry-phone-input" /></Field>
        <Field label="Email" name="email" required error={errors.email}><input className={inp("email")} type="email" value={form.email} onChange={set("email")} autoComplete="email" data-testid="enquiry-email-input" /></Field>
        <Field label="Service Required" name="service" required error={errors.service}>
          <select className={inp("service")} value={form.service} onChange={set("service")} data-testid="enquiry-service-select">
            <option value="">Select a service</option>
            {FORM_SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Current Country" name="country" required error={errors.country}><input className={inp("country")} value={form.country} onChange={set("country")} autoComplete="country-name" data-testid="enquiry-country-input" /></Field>
        <Field label="Current Immigration Status in Canada" name="status_in_canada">
          <select className="field" value={form.status_in_canada} onChange={set("status_in_canada")} data-testid="enquiry-status-select">
            <option value="">Select if applicable</option>
            {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Work Permit Expiry Date" name="work_permit_expiry"><input className="field" type="date" value={form.work_permit_expiry} onChange={set("work_permit_expiry")} data-testid="enquiry-expiry-input" /></Field>
        <Field label="Preferred Contact Method" name="contact_method">
          <select className="field" value={form.contact_method} onChange={set("contact_method")} data-testid="enquiry-contact-method-select">
            {CONTACT_METHODS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Preferred Consultation Date" name="consultation_date"><input className="field" type="date" value={form.consultation_date} onChange={set("consultation_date")} data-testid="enquiry-consultation-date-input" /></Field>
      </div>
      <Field label="Message" name="message" required error={errors.message}>
        <textarea className={`${inp("message")} min-h-[120px]`} rows={5} value={form.message} onChange={set("message")} placeholder="Briefly describe your situation and what you would like help with." data-testid="enquiry-message-input" />
      </Field>
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} name="website" data-testid="enquiry-honeypot" /></label>
      </div>
      {serverError && <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-brand-red" role="alert" data-testid="enquiry-server-error">{serverError}</p>}
      <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">By sending, you agree to our <a href="/privacy-policy" className="underline">Privacy Policy</a>. Sending an enquiry does not create a retainer.</p>
        <button type="submit" disabled={state === "sending"} className="btn-primary justify-center disabled:opacity-70" data-testid="enquiry-submit-btn">
          {state === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send an Enquiry <Send className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
};
